import React, { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { authService } from '../../services/authService';
import type { TotpSignInChallenge } from '../../services/authService';
import type { User } from 'firebase/auth';
import { Button, Input, Alert, Spinner } from '../common/UIComponents';
import GoogleSignInButton from './GoogleSignInButton';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRightIcon,
  EnvelopeIcon,
  LockClosedIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import PasswordInput from '../common/PasswordInput';
import {
  decodePostAuthIntentFromQuery,
  getPostAuthIntent,
  savePostAuthIntentFromQueryValue,
} from '../../utils/postAuthIntent';

type LoginFormData = {
  email: string;
  password: string;
};

interface LoginFormProps {
  initialEmail?: string;
  initialHint?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({ initialEmail, initialHint }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: {
      email: initialEmail || '',
      password: '',
    },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialHint || null);
  const [mfaChallenge, setMfaChallenge] = useState<TotpSignInChallenge | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();
  const emailValue = watch('email');
  const queryParams = new URLSearchParams(location.search);
  const postAuthIntentFromQuery = queryParams.get('postAuthIntent');
  useEffect(() => {
    if (!postAuthIntentFromQuery) {
      return;
    }
    savePostAuthIntentFromQueryValue(postAuthIntentFromQuery, 'login-form-query');
  }, [postAuthIntentFromQuery]);

  const resetPasswordUrl = emailValue
    ? `/reset-password?email=${encodeURIComponent(emailValue)}${postAuthIntentFromQuery ? `&postAuthIntent=${postAuthIntentFromQuery}` : ''}`
    : `/reset-password${postAuthIntentFromQuery ? `?postAuthIntent=${postAuthIntentFromQuery}` : ''}`;

  const handleAuthenticatedUser = useCallback(
    (user: User, fallbackEmail?: string) => {
      if (!user.emailVerified) {
        const intentPath =
          decodePostAuthIntentFromQuery(postAuthIntentFromQuery) || getPostAuthIntent();
        const verifyFrom = intentPath ? { pathname: intentPath } : { pathname: '/app' };
        const verifyEmailTarget = postAuthIntentFromQuery
          ? `/verify-email?postAuthIntent=${postAuthIntentFromQuery}`
          : '/verify-email';
        navigate(verifyEmailTarget, {
          state: {
            email: user.email || fallbackEmail || '',
            from: verifyFrom,
          },
          replace: true,
        });
      }
    },
    [navigate, postAuthIntentFromQuery]
  );

  const handleLoginSubmit = useCallback(
    async (data: LoginFormData) => {
      if (!executeRecaptcha) {
        console.error('[LoginForm] Funkcja executeRecaptcha nie jest jeszcze załadowana');
        setError('Błąd inicjalizacji reCAPTCHA. Spróbuj odświeżyć stronę.');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        await executeRecaptcha('login');

        const user = await authService.login(data.email, data.password);
        handleAuthenticatedUser(user, data.email);
      } catch (err: any) {
        console.error('[LoginForm] Błąd logowania:', err);
        if (authService.isMfaRequiredError(err)) {
          setMfaChallenge(err.mfaChallenge);
          setMfaCode('');
          setError(null);
          return;
        }
        if (err.code && typeof authService.mapFirebaseError === 'function') {
          setError(authService.mapFirebaseError(err.code));
        } else {
          setError('Wystąpił nieoczekiwany błąd podczas logowania. Spróbuj ponownie za chwilę.');
        }
      } finally {
        setLoading(false);
      }
    },
    [executeRecaptcha, setError, setLoading, handleAuthenticatedUser]
  );

  const handleMfaSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!mfaChallenge) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const user = await authService.completeTotpSignIn(mfaChallenge, mfaCode);
        setMfaChallenge(null);
        setMfaCode('');
        handleAuthenticatedUser(user, user.email || emailValue);
      } catch (err: any) {
        console.error('[LoginForm] Błąd potwierdzenia MFA:', err);
        setError(
          err.code
            ? authService.mapFirebaseError(err.code)
            : 'Nie udało się potwierdzić kodu. Spróbuj ponownie.'
        );
      } finally {
        setLoading(false);
      }
    },
    [emailValue, handleAuthenticatedUser, mfaChallenge, mfaCode]
  );

  if (mfaChallenge) {
    return (
      <>
        <form onSubmit={handleMfaSubmit} className="animate-fade-in">
          {error && (
            <Alert
              variant="error"
              icon={<ExclamationCircleIcon className="w-5 h-5" />}
              className="mb-6"
            >
              {error}
            </Alert>
          )}
          <Alert variant="info" className="mb-5">
            Konto wymaga kodu z aplikacji uwierzytelniającej.
          </Alert>
          <Input
            id="mfaCode"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            label="Kod weryfikacyjny"
            placeholder="123456"
            value={mfaCode}
            onChange={(event) => setMfaCode(event.target.value)}
            icon={<LockClosedIcon className="w-5 h-5 text-primary-light-400/80" />}
            required
            disabled={loading}
            className="py-2.5"
          />
          <div className="mt-8 space-y-3">
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={loading || !mfaCode.trim()}
              className="py-2.5 text-base font-medium"
            >
              {loading ? (
                <>
                  <Spinner size="sm" color="white" className="mr-2" />
                  Sprawdzanie...
                </>
              ) : (
                'Potwierdź logowanie'
              )}
            </Button>
            <Button
              type="button"
              variant="neutral"
              fullWidth
              disabled={loading}
              onClick={() => {
                setMfaChallenge(null);
                setMfaCode('');
                setError(null);
              }}
            >
              Wróć do logowania
            </Button>
          </div>
        </form>
      </>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit(handleLoginSubmit)} className="animate-fade-in">
        {error && (
          <Alert
            variant="error"
            icon={<ExclamationCircleIcon className="w-5 h-5" />}
            className="mb-6"
          >
            {error}
          </Alert>
        )}
        <GoogleSignInButton
          onError={setError}
          onMfaRequired={(challenge) => {
            setMfaChallenge(challenge);
            setMfaCode('');
            setError(null);
          }}
        />
        <div className="relative my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-primary-dark-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-primary-dark-100 text-primary-light-400">lub</span>
          </div>
        </div>
        <div className="space-y-5">
          <Input
            id="email"
            type="email"
            label="Adres email"
            placeholder="twoj@email.pl"
            icon={<EnvelopeIcon className="w-5 h-5 text-primary-light-400/80" />}
            error={errors.email?.message}
            required
            className="py-2.5"
            {...register('email', {
              required: 'Email jest wymagany',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Nieprawidłowy format adresu email',
              },
            })}
          />

          <PasswordInput
            id="password"
            label="Hasło"
            placeholder="••••••••"
            icon={<LockClosedIcon className="w-5 h-5 text-primary-light-400/80" />}
            error={errors.password?.message}
            required
            autoComplete="current-password"
            className="py-2.5"
            {...register('password', {
              required: 'Hasło jest wymagane',
              minLength: {
                value: 6,
                message: 'Hasło musi mieć co najmniej 6 znaków',
              },
            })}
          />
          <div className="flex justify-end">
            <Link
              to={resetPasswordUrl}
              className="text-sm text-ratuj-300 hover:text-ratuj-200 focus-outline rounded"
            >
              Nie pamiętasz hasła?
            </Link>
          </div>
        </div>
        <div className="mt-8">
          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={loading || !executeRecaptcha}
            className="py-2.5 text-base font-medium"
          >
            {loading ? (
              <>
                <Spinner size="sm" color="white" className="mr-2" />
                Logowanie...
              </>
            ) : (
              <>
                <span className="inline-block mr-1">Zaloguj się</span>
                <ArrowRightIcon className="ml-1 inline-block h-5 w-5" />
              </>
            )}
          </Button>
        </div>
      </form>
      {!executeRecaptcha && (
        <p style={{ fontSize: '0.8em', color: 'orange', marginTop: '10px', textAlign: 'center' }}>
          Inicjalizowanie zabezpieczeń...
        </p>
      )}
      <p className="text-xs text-primary-light-400/70 mt-6 text-center">
        Ta strona jest chroniona przez reCAPTCHA i obowiązują ją
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-light-300 hover:text-primary-light-200 underline mx-1"
        >
          Polityka Prywatności
        </a>
        oraz
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-light-300 hover:text-primary-light-200 underline ml-1"
        >
          Warunki Korzystania z Usługi
        </a>
        Google.
      </p>
    </>
  );
};

export default LoginForm;
